import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, MapPin, CalendarCheck, Users, Sparkles, ArrowUpRight } from 'lucide-react';
import { formatClientCount } from '../utils/stats';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FreshnessIndicator — Live activity widget.
 *
 * Framing: forward-looking + recurring (not yearly totals).
 *   - "10 interventions ce mois-ci"
 *   - "Prochain passage : demain à Coëx"
 *   - "6 clients en contrat d'entretien régulier"
 *
 * Variants:
 *   - "pill"   : compact hero pill (always on light bg)
 *   - "banner" : zone-page activity card with multiple KPIs
 *   - "strip"  : stat strip — supports tone="light" (default) and tone="dark"
 */

const SERVICE_PRESENT = {
  tonte: 'tonte',
  'taille-haies': 'taille de haies',
  debroussaillage: 'débroussaillage',
  desherbage: 'désherbage',
  nettoyage: 'nettoyage anti-mousse',
  'entretien-general': 'entretien',
};

const formatDateFR = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  } catch {
    return iso;
  }
};

const FreshnessIndicator = ({ variant = 'pill', townSlug, tone = 'light', className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const url = new URL(`${API}/api/public/freshness`);
    if (townSlug) url.searchParams.set('town_slug', townSlug);
    fetch(url.toString())
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [townSlug]);

  if (loading || !data || !data.headline) return null;

  const { headline, global: g, town } = data;
  const serviceText = SERVICE_PRESENT[headline.service] || 'intervention';
  const isFuture = headline.status === 'scheduled';

  // ----- PILL --------------------------------------------------------------
  if (variant === 'pill') {
    const label = isFuture
      ? `Prochain passage ${headline.ago} à ${headline.town_display}`
      : `En tournée ${headline.ago} à ${headline.town_display}`;
    return (
      <div
        data-testid="freshness-indicator-pill"
        className={`inline-flex items-center gap-2 bg-white border border-emerald-300 text-emerald-900 text-sm font-medium px-3.5 py-1.5 rounded-full shadow-sm ${className}`}
        aria-live="polite"
      >
        <span className="relative flex h-2 w-2" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="truncate">{label}</span>
      </div>
    );
  }

  // ----- BANNER (zone pages) ----------------------------------------------
  if (variant === 'banner') {
    const t = town || {};
    const townHasActivity = (t.year_jobs ?? 0) > 0;
    // When the town has no recorded activity yet, fall back to site-wide
    // numbers so we never show "0 / 0 / 0" (which makes the page look dead).
    const next = townHasActivity ? t.next_job : (headline.status === 'scheduled' ? headline : null);
    const last = townHasActivity ? t.last_job : (headline.status !== 'scheduled' ? headline : null);
    const hasUpcoming = next && Object.keys(next).length > 0;

    const monthJobs = townHasActivity ? (t.month_jobs ?? 0) : g.month_jobs;
    const recurring = townHasActivity ? (t.recurring_clients ?? 0) : formatClientCount(g.total_clients ?? g.recurring_clients);
    const upcoming = townHasActivity ? (t.upcoming_7d ?? 0) : g.upcoming_7d;
    const scopeLabel = townHasActivity ? 'Activité locale' : 'Activité Vendée';

    return (
      <div
        data-testid="freshness-indicator-banner"
        className={`rounded-2xl border-2 border-emerald-300 bg-white p-5 shadow-md ${className}`}
        aria-live="polite"
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
              {scopeLabel} en direct
            </span>
          </div>
          {!townHasActivity && t.display && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
              {t.display} — bientôt&nbsp;?
            </span>
          )}
        </div>

        <div className={`grid ${upcoming < 5 ? 'grid-cols-2' : 'grid-cols-3'} gap-3 mb-4`}>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-700 tabular-nums leading-none">
              {monthJobs}
            </div>
            <div className="text-[11px] text-gray-600 mt-1 leading-tight">
              interventions<br />ce mois-ci
            </div>
          </div>
          <div className={`text-center ${upcoming < 5 ? 'border-l' : 'border-x'} border-gray-200`}>
            <div className="text-2xl font-bold text-emerald-700 tabular-nums leading-none">
              {recurring}
            </div>
            <div className="text-[11px] text-gray-600 mt-1 leading-tight">
              clients<br />réguliers
            </div>
          </div>
          {upcoming >= 5 && (
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-700 tabular-nums leading-none">
                {upcoming}
              </div>
              <div className="text-[11px] text-gray-600 mt-1 leading-tight">
                passages prévus<br />cette semaine
              </div>
            </div>
          )}
        </div>
        {upcoming < 5 && (
          <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-center text-[13px] font-semibold text-amber-800" data-testid="planning-sollicite">
            📍 Planning très sollicité : quelques créneaux encore disponibles cette semaine.
          </div>
        )}

        <div className="pt-3 border-t border-gray-100 space-y-1.5 text-sm">
          {hasUpcoming && (
            <DetailLine
              icon={<CalendarCheck size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden="true" />}
              townSlug={!townHasActivity ? next.town_slug : null}
              testid="freshness-next-link"
            >
              <span>
                Prochain passage : <strong>{next.ago}</strong>
                {next.date_intervention ? ` · ${formatDateFR(next.date_intervention)}` : ''}
                {' '}{next.town_display && !townHasActivity ? `à ${next.town_display} ` : ''}
                — {SERVICE_PRESENT[next.service] || next.service_label?.toLowerCase()}
              </span>
            </DetailLine>
          )}
          {last && (
            <DetailLine
              icon={<Activity size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" aria-hidden="true" />}
              townSlug={!townHasActivity ? last.town_slug : null}
              muted
              testid="freshness-last-link"
            >
              <span>
                Dernière intervention : <strong>{last.ago}</strong>
                {' '}{last.town_display && !townHasActivity ? `à ${last.town_display} ` : ''}
                — {SERVICE_PRESENT[last.service] || last.service_label?.toLowerCase()}
              </span>
            </DetailLine>
          )}
        </div>
      </div>
    );
  }

  // ----- STRIP -------------------------------------------------------------
  if (variant === 'strip') {
    const dark = tone === 'dark';
    const numCls = dark ? 'text-white' : 'text-emerald-700';
    const textCls = dark ? 'text-emerald-50' : 'text-gray-700';
    const sepCls = dark ? 'bg-white/30' : 'bg-gray-300';
    const iconCls = dark ? 'text-lime-300' : 'text-emerald-600';

    const upcomingNote = headline?.ago
      ? (headline.status === 'scheduled'
        ? `Prochain passage ${headline.ago}`
        : `En tournée ${headline.ago}`)
      : null;

    return (
      <div
        data-testid="freshness-indicator-strip"
        className={`flex flex-wrap items-center justify-center gap-x-6 gap-y-3 ${className}`}
      >
        <div className={`inline-flex items-center gap-2 ${textCls}`}>
          <CalendarCheck size={18} className={iconCls} aria-hidden="true" />
          <span className="text-sm">
            <strong className={`${numCls} text-lg tabular-nums`}>{g.month_jobs}</strong>{' '}
            interventions ce mois-ci
          </span>
        </div>
        <div className={`hidden sm:block h-5 w-px ${sepCls}`} aria-hidden="true" />
        <div className={`inline-flex items-center gap-2 ${textCls}`}>
          <Users size={18} className={iconCls} aria-hidden="true" />
          <span className="text-sm">
            <strong className={`${numCls} text-lg tabular-nums`}>{formatClientCount(g.total_clients ?? g.recurring_clients)}</strong>{' '}
            clients réguliers
          </span>
        </div>
        <div className={`hidden sm:block h-5 w-px ${sepCls}`} aria-hidden="true" />
        <div className={`inline-flex items-center gap-2 ${textCls}`}>
          <MapPin size={18} className={iconCls} aria-hidden="true" />
          <span className="text-sm">
            <strong className={`${numCls} text-lg tabular-nums`}>{g.coverage_town_count ?? g.town_count}</strong>{' '}
            communes desservies
          </span>
        </div>
        {upcomingNote && (
          <>
            <div className={`hidden md:block h-5 w-px ${sepCls}`} aria-hidden="true" />
            <div className={`inline-flex items-center gap-2 ${textCls}`}>
              <Sparkles size={16} className={iconCls} aria-hidden="true" />
              <span className="text-sm">
                {upcomingNote}{' '}
                <strong className={numCls}>à {headline.town_display}</strong>
              </span>
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
};

/**
 * One activity-detail line. If `townSlug` is provided, the whole line becomes
 * a router Link pointing to `/zone/{townSlug}` — turning fallback banners into
 * internal-linking funnels toward higher-activity town pages (SEO win).
 */
const DetailLine = ({ icon, townSlug, muted = false, testid, children }) => {
  const baseTone = muted ? 'text-gray-600' : 'text-gray-800';
  const interactive = Boolean(townSlug);
  const content = (
    <>
      {icon}
      {children}
      {interactive && (
        <ArrowUpRight
          size={13}
          className="text-emerald-600 ml-auto flex-shrink-0 mt-0.5 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
          aria-hidden="true"
        />
      )}
    </>
  );
  if (interactive) {
    return (
      <Link
        to={`/zone/${townSlug}`}
        data-testid={testid}
        className={`group flex items-start gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-emerald-50 transition-colors ${baseTone} hover:text-emerald-900`}
      >
        {content}
      </Link>
    );
  }
  return <p className={`flex items-start gap-2 ${baseTone}`}>{content}</p>;
};

export default FreshnessIndicator;
